/**
 * Refund a receipt — FR-E13, and the arithmetic in front of the person. Split
 * out of `Receipts.tsx` and laid out as the Branches dialog in Wave 15D (C14
 * §10.6 item 7; T-6.16). Behaviour unchanged: the reason, the split, the
 * gateway's refusal and the step-up are all where they were.
 *
 * **The refund lives on the receipt, not on the invoice** — ADR-0011 §8. A
 * refund reverses a *receipt*, in whole or in part, and which document it needs
 * falls out of that receipt rather than out of a choice somebody makes: the
 * part of it still held as an advance reverses nothing and goes back on its
 * own, and anything beyond that reversed an invoiced charge and needs a credit
 * note for exactly that much. The dialog splits the amount the way the server
 * will, so the invoice picker appears only when there is a second part, and the
 * sentence beneath says what will be credited. The server refuses if the two do
 * not agree to the paisa.
 *
 * **A gateway receipt cannot be refunded while the gateway is off**, and it
 * says so before the button is pressed rather than after. The money has to go
 * back the way it came (ADR-0011 §8); recording a ledger entry for a refund
 * that never left the bank would put a refund on a client's statement that they
 * never received.
 *
 * **And while the gateway is on, it is only requested** (C10, ADR-0027 §5):
 * the gateway is asked, the receipt reads *refund requested*, and the ledger
 * entry and the credit note are written when the gateway confirms the money has
 * gone back. The dialog says so before the step-up rather than after.
 *
 * **C27 (ADR-0056 §7; UAT reports #9, #16):** *Refund* sends first and asks
 * for a code only when the service answers `STEPUP_REQUIRED` (`useStepUp`, the
 * publish pattern), then sends once more with the assertion. The split, the
 * reason and the invoice are still settled here before anything is sent.
 */

import { Alert, Button, Dialog, Input, Select, Textarea } from '@fiveox/ui'
import { useCallback, useEffect, useState } from 'react'

import { StepUpDialog, useStepUp } from '../../components/StepUpDialog'
import { FORM_GRID, FormRow } from '../../components/page'
import { ApiError } from '../../lib/api'
import {
  type BillingVocabulary,
  type Invoice,
  type Receipt,
  billing,
  gatewayReason,
  refundOutcome,
  rupees,
} from '../../lib/api/billing'
import { DialogFooter } from './shared'

export function RefundDialog({
  receipt,
  vocab,
  onClose,
  onRefunded,
}: {
  receipt: Receipt
  vocab: BillingVocabulary | null
  onClose: () => void
  onRefunded: (message: string) => void
}) {
  const [amount, setAmount] = useState(receipt.amount)
  const [reason, setReason] = useState('')
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [invoiceId, setInvoiceId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const refused = useCallback((err: unknown) => {
    setError(err instanceof ApiError ? err.message : 'The refund could not be recorded.')
  }, [])
  const stepUp = useStepUp(refused)

  useEffect(() => {
    void billing
      .invoices({ client_id: receipt.client_id, status: 'issued', limit: 100 })
      .then((r) => setInvoices(r.data))
      .catch(() => setInvoices([]))
  }, [receipt.client_id])

  // The same split the server makes, so the screen and the refusal agree.
  // `Number` is acceptable here and only here: nothing is written back from it
  // — the string the firm typed is what goes on the request.
  const held = Number(receipt.unapplied_amount)
  const wanted = Number(amount || '0')
  const fromAdvance = Math.max(0, Math.min(wanted, held))
  const fromInvoiced = Math.max(0, wanted - fromAdvance)
  const needsCredit = fromInvoiced > 0
  const viaGateway = receipt.gateway_payment
  const gatewayBlocked = viaGateway && !vocab?.gateway_active

  /** The gated action: its refusals are thrown for `useStepUp` to sort. */
  const refund = async (assertion?: string) => {
    const resp = await billing.refund(receipt.id, assertion, {
      amount: amount.trim(),
      reason: reason.trim(),
      invoice_id: needsCredit ? invoiceId : null,
    })
    onRefunded(refundOutcome(resp.data))
  }

  const start = async () => {
    setBusy(true)
    setError(null)
    try {
      await stepUp.attempt(refund)
    } finally {
      setBusy(false)
    }
  }

  // Why Continue is not ready yet, in the firm's words — a disabled control
  // says why (the Wave 15 design bar).
  const why = gatewayBlocked
    ? 'The gateway this came through is off.'
    : wanted <= 0
      ? 'Type an amount to refund.'
      : !reason.trim()
        ? 'Give the reason — it is printed on the credit note.'
        : needsCredit && !invoiceId
          ? 'Choose the invoice to credit.'
          : undefined

  return (
    <>
      {/* The kit's dialog. It steps aside while the step-up is open: two kit
          dialogs at once would each read a click in the other as an outside
          click and close. Its fields are this component's state, so nothing
          typed is lost, and a refused refund brings it back with the error
          pinned under the title. */}
      <Dialog
        open={!stepUp.challenging}
        onClose={onClose}
        title={`Refund ${receipt.number}`}
        error={error}
        width="lg"
      >
        {gatewayBlocked && (
          <Alert tone="warning" className="mb-4">
            This came in through your payment gateway, so the refund has to go back the same way —
            and {gatewayReason(vocab?.gateway_reason ?? null).toLowerCase()}
          </Alert>
        )}

        <div className={FORM_GRID}>
          <FormRow label="Amount to refund" htmlFor="refund-amount" required>
            <Input
              id="refund-amount"
              required
              aria-required="true"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              hint={`${rupees(receipt.amount)} received, ${rupees(receipt.unapplied_amount)} still held as an advance.`}
            />
          </FormRow>
          {needsCredit && (
            <FormRow label="Credit which invoice" htmlFor="refund-invoice" required>
              <Select
                id="refund-invoice"
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
                required
                aria-required="true"
              >
                {/* A placeholder for a required value, not a choice (UAT #13). */}
                <option value="" disabled hidden>
                  Choose an invoice…
                </option>
                {invoices.map((invoice) => (
                  <option key={invoice.id} value={invoice.id}>
                    {invoice.number} — {rupees(invoice.grand_total)}
                  </option>
                ))}
              </Select>
            </FormRow>
          )}
          <FormRow label="Reason" htmlFor="refund-reason" required>
            <Textarea
              id="refund-reason"
              required
              aria-required="true"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              hint="Printed on the credit note, and the first thing anybody asks about a refund."
            />
          </FormRow>
        </div>

        <div className="mt-5 space-y-3">
          <Alert tone="info">
            {needsCredit
              ? `${rupees(fromAdvance.toFixed(2))} comes out of the advance and reverses nothing. ${rupees(fromInvoiced.toFixed(2))} reverses an invoiced charge, so a credit note for that exact amount is issued against the invoice you choose — crediting every line in full, which cancels it.`
              : 'The whole of this comes out of the advance your firm is holding. Nothing was invoiced for it, so no credit note is issued.'}
          </Alert>
          {viaGateway && !gatewayBlocked && (
            <Alert tone="info">
              This goes back through your payment gateway. The refund is requested now and
              recorded on the client&apos;s account — with its credit note — when the gateway
              confirms the money has gone back. Until then the receipt reads &ldquo;refund
              requested&rdquo;.
            </Alert>
          )}
        </div>

        <DialogFooter onCancel={onClose} busy={busy}>
          <span title={why}>
            <Button
              onClick={() => void start()}
              disabled={busy || why !== undefined}
              loading={busy && !stepUp.challenging}
            >
              Continue
            </Button>
          </span>
        </DialogFooter>
      </Dialog>

      <StepUpDialog
        {...stepUp.dialog}
        title="Refund this receipt"
        what={
          viaGateway
            ? "Your payment gateway is asked to send this money back, and that cannot be undone from here. When it confirms, the client's ledger is debited for the refund and, where it reverses an invoiced charge, a credit note is issued and takes the next number in your series."
            : "Money leaving your firm cannot be undone from here. The client's ledger is debited for the refund, and where it reverses an invoiced charge a credit note is issued and takes the next number in your series."
        }
      />
    </>
  )
}
